<div class="pad-page">
  <div class="pad-header">
    <div>
      <h1>BoQ planned vs actual</h1>
      <p *ngIf="data?.boq">
        {{ data.boq.project_name }} · {{ data.boq.boq_code }} — {{ data.boq.boq_name }}
      </p>
    </div>
    <app-back-button label="Back to BoQ list" [link]="['/home/boq']"></app-back-button>
  </div>

  <div class="pad-card pad-center" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading deviation report…</p>
  </div>

  <div class="pad-card pad-center" *ngIf="!loading && error">
    <mat-icon class="text-red-500">error_outline</mat-icon>
    <p>{{ error }}</p>
  </div>

  <ng-container *ngIf="!loading && data">
    <div class="pad-chips">
      <div class="pad-chip">
        <span class="pad-chip-label">BoQ total</span>
        <span class="pad-chip-value">{{ formatCurrency(data.boq.total_amount, data.boq.currency) }}</span>
      </div>
      <div class="pad-chip">
        <span class="pad-chip-label">Bid coverage</span>
        <span class="pad-chip-value">{{ data.summary.bid_deviation_count }} deviations</span>
      </div>
      <div class="pad-chip">
        <span class="pad-chip-label">Execution coverage</span>
        <span class="pad-chip-value">{{ data.summary.execution_coverage_pct }}% of items linked</span>
      </div>
      <div class="pad-chip">
        <span class="pad-chip-label">Invoiced</span>
        <span class="pad-chip-value">{{ formatCurrency(data.summary.invoiced_total, data.boq.currency) }}</span>
      </div>
      <div class="pad-chip">
        <span class="pad-chip-label">Overall amount variance</span>
        <span class="pad-chip-value" [ngClass]="varianceClass(data.summary.invoice_amount_variance)">
          {{ signedCurrency(data.summary.invoice_amount_variance, data.boq.currency) }}
        </span>
      </div>
    </div>

    <p class="pad-note">
      Execution and invoice figures only include work and invoices already linked to a BoQ item.
      Unlinked items are not “on plan” — coverage above shows how complete the mapping is.
    </p>

    <mat-tab-group animationDuration="0" class="pad-tabs">
      <mat-tab label="Bids">
        <div class="pad-card" *ngIf="!data.bids?.length">
          <p class="pad-empty">No bid deviations for this BoQ. Either no bids have been submitted, or all submitted quantities and rates match the estimate.</p>
        </div>
        <div class="pad-card" *ngIf="data.bids?.length">
          <div class="overflow-x-auto">
            <table class="pad-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="num">Est. qty</th>
                  <th class="num">Est. rate</th>
                  <th class="num">Bid qty</th>
                  <th class="num">Bid rate</th>
                  <th class="num">Amount variance</th>
                  <th>Bidder</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let d of data.bids">
                  <td>
                    <div class="item-name">{{ d.item_description }}</div>
                    <div class="item-code" *ngIf="d.item_code">{{ d.item_code }}</div>
                  </td>
                  <td class="num">{{ formatQty(d.estimated_quantity) }}</td>
                  <td class="num">{{ formatCurrency(d.estimated_unit_rate, data.boq.currency) }}</td>
                  <td class="num">{{ formatQty(d.bidder_quantity) }}</td>
                  <td class="num">{{ formatCurrency(d.bidder_unit_rate, data.boq.currency) }}</td>
                  <td class="num" [ngClass]="varianceClass(d.amount_deviation)">
                    {{ signedCurrency(d.amount_deviation, data.boq.currency) }}
                  </td>
                  <td>{{ d.bidder_org_name || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Execution">
        <div class="pad-card" *ngIf="!data.execution?.length">
          <p class="pad-empty">This BoQ has no line items.</p>
        </div>
        <div class="pad-card" *ngIf="data.execution?.length && data.summary.execution_linked_count === 0">
          <p class="pad-empty">No PLM work items are linked to this BoQ. Actual qty stays 0 until pavement work is mapped via BoQ item.</p>
        </div>
        <div class="pad-card" *ngIf="data.execution?.length">
          <div class="overflow-x-auto">
            <table class="pad-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="num">Planned qty</th>
                  <th class="num">Actual qty</th>
                  <th class="num">Qty variance</th>
                  <th class="num">Planned amount</th>
                  <th class="num">Actual amount</th>
                  <th class="num">Amount variance</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of data.execution">
                  <td>
                    <div class="item-name">{{ row.item_description }}</div>
                    <div class="item-code">{{ row.item_code }} <span *ngIf="row.section_name">· {{ row.section_name }}</span></div>
                  </td>
                  <td class="num">{{ formatQty(row.planned_qty) }} {{ row.unit }}</td>
                  <td class="num">{{ formatQty(row.actual_qty) }}</td>
                  <td class="num" [ngClass]="varianceClass(row.qty_variance)">{{ signedQty(row.qty_variance) }}</td>
                  <td class="num">{{ formatCurrency(row.planned_amount, data.boq.currency) }}</td>
                  <td class="num">{{ formatCurrency(row.actual_amount, data.boq.currency) }}</td>
                  <td class="num" [ngClass]="varianceClass(row.amount_variance)">
                    {{ signedCurrency(row.amount_variance, data.boq.currency) }}
                  </td>
                  <td>
                    <span class="link-pill" [class.link-yes]="row.link_status === 'linked'">
                      {{ row.link_status === 'linked' ? 'Linked' : 'Not linked' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Invoices">
        <div class="pad-card" *ngIf="data.summary.invoice_linked_count === 0 && !data.unallocated_invoiced">
          <p class="pad-empty">No invoices are linked to this BoQ’s items. Invoiced amounts stay 0 until PLM invoice links include a BoQ item.</p>
        </div>
        <div class="pad-card" *ngIf="data.unallocated_invoiced">
          <p>
            Unallocated invoices on this project (no BoQ item):
            <strong>{{ formatCurrency(data.unallocated_invoiced, data.boq.currency) }}</strong>
          </p>
        </div>
        <div class="pad-card" *ngIf="!data.invoices?.length">
          <p class="pad-empty">This BoQ has no line items.</p>
        </div>
        <div class="pad-card" *ngIf="data.invoices?.length">
          <div class="overflow-x-auto">
            <table class="pad-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="num">Planned amount</th>
                  <th class="num">Invoiced</th>
                  <th class="num">Variance</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of data.invoices">
                  <td>
                    <div class="item-name">{{ row.item_description }}</div>
                    <div class="item-code">{{ row.item_code }}</div>
                  </td>
                  <td class="num">{{ formatCurrency(row.planned_amount, data.boq.currency) }}</td>
                  <td class="num">{{ formatCurrency(row.invoiced_amount, data.boq.currency) }}</td>
                  <td class="num" [ngClass]="varianceClass(row.amount_variance)">
                    {{ signedCurrency(row.amount_variance, data.boq.currency) }}
                  </td>
                  <td>
                    <span class="link-pill" [class.link-yes]="row.link_status === 'linked'">
                      {{ row.link_status === 'linked' ? 'Linked' : 'Not linked' }}
                    </span>
                  </td>
                </tr>
                <tr *ngIf="data.unallocated_invoiced" class="remainder">
                  <td>Unallocated invoices</td>
                  <td class="num">—</td>
                  <td class="num">{{ formatCurrency(data.unallocated_invoiced, data.boq.currency) }}</td>
                  <td class="num var-over">{{ signedCurrency(data.unallocated_invoiced, data.boq.currency) }}</td>
                  <td><span class="link-pill">Not linked</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </mat-tab>
    </mat-tab-group>
  </ng-container>
</div>
